import React, { useEffect, useState } from 'react'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import debounce from 'debounce-promise'
import messages from "./messages";
import {FormattedMessage, useIntl} from "react-intl";
import useCurrentUser from 'hooks/useCurrentUser'

const FinanceTerms = ({ state, setState }) => {
  const currentUser = useCurrentUser()

  // The account owner is saved on its own, not through the page's Save button, so it is
  // kept out of the parent state - writing to it there would mark the whole form dirty.
  const [owner, setOwner] = useState(null)
  const [ownerOptions, setOwnerOptions] = useState([])
  const [ownerEditable, setOwnerEditable] = useState(false)
  const [ownerContext, setOwnerContext] = useState({ openOrders: 0, economicId: '' })
  const [movingOwner, setMovingOwner] = useState(false)

  const loadOptions = debounce((query) => {
    return new Promise((resolve, reject) => {
      fetch('/rest/v2/accounting_payment_term?pageSize=100')
        .then(response => response.json())
        .then((list) => {
          resolve(list.map((item) => ({ value: item.uid, label: item.name })))
        })
        .catch(reject)
    })
  }, 250)

  useEffect(() => {
    if (state.economicDebtorGroupHandle !== '0') {
      fetch(`/rest/v1/accounting_debtor_group/${state.economicDebtorGroupHandle}`)
        .then((response) => response.json())
        .then((data) => {
          setState({...state, economicDebtorGroupHandleObject: {value: data.uid, label: data.name}});
        });
    }
  }, [])

  useEffect(() => {
    fetch(`/rest/v2/customer/${state.uid}/partner`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ data }) => {
        if (!data) {
          return
        }

        setOwner(data.partner)
        setOwnerOptions(data.partners.map(({ uid, name }) => ({ value: uid, label: name })))
        setOwnerEditable(Boolean(data.editable))
        setOwnerContext({
          openOrders: data.openOrders ?? 0,
          economicId: data.economicId ?? '',
        })
      })
      .catch((error) => console.error(error))
  }, [])

  const { formatMessage } = useIntl()

  const moveOwner = ({ value, label }) => {
    if (value === owner) {
      return
    }

    const from = ownerOptions.find((option) => option.value === owner)?.label ?? owner

    const confirmation = [
      formatMessage(messages.ownerConfirm, { customerName: state.name, from, to: label }),
      '',
      '• ' + (ownerContext.economicId
        ? formatMessage(messages.ownerConfirmEconomic, { economicId: ownerContext.economicId })
        : formatMessage(messages.ownerConfirmNoEconomic)),
      '• ' + formatMessage(messages.ownerConfirmOrders, { count: ownerContext.openOrders }),
      '• ' + formatMessage(messages.ownerConfirmContacts),
    ].join('\n')

    if (!window.confirm(confirmation)) {
      return
    }

    setMovingOwner(true)

    fetch(`/rest/v2/customer/${state.uid}/partner`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify({ partner: value }),
    })
      .then((response) => response.json())
      .then(({ success, data, errors }) => {
        if (!success) {
          throw new Error((errors ?? []).join(', '))
        }

        setOwner(data.partner)
        // Cleared by the move, so the next confirm must not offer it again.
        setOwnerContext((context) => ({ ...context, economicId: '' }))
      })
      .catch((error) => window.alert(formatMessage(messages.ownerFailed, { error: error.message })))
      .finally(() => setMovingOwner(false))
  }

  return (
    <div className="mb-6 grid grid-cols-2 gap-4">
      <div>
        <div className="text-gray-500 mb-2">Payment Terms</div>
        <AsyncSelect
          value={{ label: state.payment_terms.name, value: parseInt(state.payment_terms.uid) }}
          onChange={({ value, label }) => setState((state) => ({
            ...state,
            economicTermOfPaymentHandle: value,
            payment_terms: {
              name: label,
              uid: value,
            }
          }))}
          loadOptions={loadOptions}
          openMenuOnFocus
          defaultOptions
        />
      </div>

      <div>
        <div className="text-gray-500 mb-2">
          <span><FormattedMessage {...messages.owner}/>
            (<a
              onClick={() => window.alert(formatMessage(ownerEditable ? messages.ownerAlertFinance : messages.ownerAlert))}
              className="text-primary cursor-pointer"
            >
                <FormattedMessage{...messages.why}/>
            </a>)
          </span>
        </div>
        <Select
          value={ownerOptions.find((option) => option.value === owner) ?? null}
          onChange={moveOwner}
          openMenuOnFocus
          options={ownerOptions}
          isDisabled={!ownerEditable || movingOwner}
          isLoading={movingOwner}
          placeholder={movingOwner ? formatMessage(messages.ownerSaving) : undefined}
        />
      </div>

      { currentUser.roles.includes("finance") &&
        <div>
          <div className="text-gray-500 mb-2"><FormattedMessage {...messages.economicDebtorGroup}/></div>
          <AsyncSelect
            value={state.economicDebtorGroupHandleObject ? state.economicDebtorGroupHandleObject : null}
            onChange={( selection ) => setState((state) => ({
              ...state,
              economicDebtorGroupHandle: selection.value,
              economicDebtorGroupHandleObject: selection
            }))}
            loadOptions={
              debounce((query) => fetch(`/rest/v1/accounting_debtor_group?search[name]=${query}`)
                .then((response) => response.json())
                .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
              , 250)
            }
            openMenuOnFocus
            defaultOptions
          />
        </div>
      }
    </div>
  )
}

export default FinanceTerms
